import moment from 'moment';
import { ReactNode } from 'react';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import {
	Card,
	CheckmarkIcon,
	FlexGap,
	Heading3,
	IconWrapper,
	Paragraph,
	tokens,
} from '@treams/treams-ui';

export interface IEvaluationSubmissionV2 {
	id: string;
	evaluationForm: {
		title: {
			languageID: string;
			text: string;
		}[];
	};
	employee: {
		status: string;
	};
	createdOnDateTimeOffsetUtc: Date;
}

export interface ILearningEvent {
	id: string;
	title: {
		languageID: string;
		text: string;
	}[];
	learningType: {
		id: string;
	};
	createdOnDateTimeOffsetUtc: Date;
}

export type JourneyItem =
	| graphql.Goal
	| IEvaluationSubmissionV2
	| graphql.FeedbackRequest
	| ILearningEvent;

interface IProps {
	to: string;
	icon: ReactNode;
	title: string;
	description: string;
	checklistItems?: Array<graphql.GoalChecklistItem>;
	createdAt: Date;
}

const Item = styled.div`
	min-width: 0;
	height: 100%;

	> a {
		display: block;
		height: 100%;
	}
`;

const Copy = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${tokens.gap.element}px;
	min-width: 0;
`;

const Meta = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${tokens.gap.element}px;
	min-width: 0;
`;

const Name = styled.div`
	display: flex;
	align-items: center;
	gap: ${tokens.gap.element}px;
	flex: 1 1 auto;
	min-width: 0;
`;

const Title = styled(Heading3)`
	min-width: 0;
`;

const Date = styled(Paragraph)`
	flex-shrink: 0;
	white-space: nowrap;
`;

const JourneyListItem = ({ to, icon, title, description, createdAt, checklistItems }: IProps) => {
	const checkedItems = (checklistItems ?? []).filter((checklistItem) => checklistItem.completedBy);

	return (
		<Item>
			<Card link={to} height="100%">
				<Copy>
					<Meta>
						<Name>
							<IconWrapper size={20} color={tokens.icon.primary}>
								{icon}
							</IconWrapper>
							<Title lineClamp={1}>{title}</Title>
						</Name>
						<Date small>{moment(createdAt).format('D MMM')}</Date>
					</Meta>
					<Paragraph>{description}</Paragraph>
					{checklistItems !== undefined && (
						<FlexGap gap={tokens.gap.element} alignItems="center">
							<IconWrapper size={16} color={tokens.icon.tertiary}>
								<CheckmarkIcon />
							</IconWrapper>
							<Paragraph small>
								{checkedItems.length}/{checklistItems.length}
							</Paragraph>
						</FlexGap>
					)}
				</Copy>
			</Card>
		</Item>
	);
};

export default JourneyListItem;
